import React, { useState } from 'react';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Alert from '@material-ui/lab/Alert';
import MenuItem from '@material-ui/core/MenuItem';
import { Link, useParams } from 'react-router-dom';
import FormSection from '../../../../components/atom/FormSection';
import { doSwaggerCall } from '../../../../hooks/useSwaggerClient';

export default function ClientEditFareharborDirect({ values, setFieldValue }) {
    const { clientId } = useParams();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [companyInfo, setCompanyInfo] = useState(null);
    const [tested, setTested] = useState(false);

    // Only use config if it's for FareharborDirect type
    const config =
        values.privateApiConfig?.type === 'FareharborDirect' ? values.privateApiConfig : {};

    const handleTestConnection = async () => {
        const { shortname, username, password } = config;

        if (!shortname || !username || !password) {
            setError('Please fill in shortname, username, and password first');
            return;
        }

        setLoading(true);
        setError('');
        setTested(false);

        try {
            const result = await doSwaggerCall('activities', 'testIntegration', {
                body: {
                    service: 'FareharborDirect',
                    credentials: { shortname, username, password }
                }
            });

            if (result.success) {
                setCompanyInfo(result.companyInfo || null);
                setTested(true);
                setError('');
            } else {
                setError(result.error || 'Connection failed');
                setCompanyInfo(null);
            }
        } catch (err) {
            setError(err?.message || 'Connection failed');
            setCompanyInfo(null);
        } finally {
            setLoading(false);
        }
    };

    const updateConfig = newValues => {
        setFieldValue('privateApiConfig', {
            ...config,
            type: 'FareharborDirect',
            ...newValues
        });
    };

    return (
        <FormSection title="FareHarbor Direct Integration">
            <Grid item xs={4}>
                <TextField
                    label="FH Shortname"
                    fullWidth
                    type="text"
                    placeholder="e.g. alohatrikke"
                    value={config.shortname || ''}
                    onChange={ev => updateConfig({ shortname: ev.target.value })}
                    helperText="Company shortname from FareHarbor URL"
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    label="FH Username"
                    fullWidth
                    type="text"
                    value={config.username || ''}
                    onChange={ev => updateConfig({ username: ev.target.value })}
                    helperText="FareHarbor dashboard login"
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    label="FH Password"
                    fullWidth
                    type="password"
                    value={config.password || ''}
                    onChange={ev => updateConfig({ password: ev.target.value })}
                    helperText="FareHarbor dashboard password"
                />
            </Grid>
            <Grid item xs={3}>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    disabled={loading}
                    onClick={handleTestConnection}
                    style={{ marginTop: 8 }}
                >
                    {loading ? <CircularProgress size={24} /> : 'Test Connection'}
                </Button>
            </Grid>
            <Grid item xs={5}>
                {error && <Alert severity="error">{error}</Alert>}
                {tested && !error && <Alert severity="success">Connection successful</Alert>}
            </Grid>
            <Grid item xs={4} style={{ display: 'flex', alignItems: 'center' }}>
                <Button
                    component={Link}
                    to={`/admin/client/${clientId}/activity_import/`}
                    variant="outlined"
                    size="small"
                >
                    Activity Import
                </Button>
            </Grid>
            <Grid item xs={4}>
                {companyInfo !== null ? (
                    <TextField
                        select
                        label="Instore Payment Type"
                        fullWidth
                        value={config.instorePaymentType || ''}
                        onChange={ev => updateConfig({ instorePaymentType: ev.target.value })}
                        helperText="Select default payment type for bookings"
                    >
                        <MenuItem value="">
                            <em>Select a payment type...</em>
                        </MenuItem>
                        {companyInfo?.instorePaymentTypes?.map(paymentType => (
                            <MenuItem key={paymentType.pk} value={paymentType.pk}>
                                {paymentType.name}
                            </MenuItem>
                        ))}
                    </TextField>
                ) : (
                    <TextField
                        label="Instore Payment Type ID"
                        fullWidth
                        type="number"
                        value={config.instorePaymentType || ''}
                        onChange={ev =>
                            updateConfig({
                                instorePaymentType: parseInt(ev.target.value, 10) || null
                            })
                        }
                        helperText="Test connection to load payment types"
                    />
                )}
            </Grid>
            <Grid item xs={4}>
                {companyInfo !== null ? (
                    <TextField
                        select
                        label="Affiliate"
                        fullWidth
                        value={config.affiliate || ''}
                        onChange={ev => {
                            let newValues = {
                                affiliate: ev.target.value
                            };
                            // Also update totalSheet if affiliate has one
                            const selectedAffiliate = companyInfo.affiliates?.find(
                                aff => aff.pk.toString() === ev.target.value.toString()
                            );
                            if (selectedAffiliate?.totalSheet) {
                                newValues['totalSheet'] = selectedAffiliate.totalSheet;
                                const totalSheetItem = companyInfo.totalSheets?.find(
                                    ts =>
                                        ts.pk.toString() === selectedAffiliate.totalSheet.toString()
                                );
                                // pk and uuid change together: an unlisted sheet
                                // (an affiliate's base sheet) must not keep the old uuid
                                newValues['totalSheetUuid'] = totalSheetItem
                                    ? totalSheetItem.uuid
                                    : null;
                            }
                            updateConfig(newValues);
                        }}
                        helperText="Select default affiliate for bookings"
                    >
                        <MenuItem value="">No affiliate</MenuItem>
                        {companyInfo?.affiliates?.map(affiliate => (
                            <MenuItem key={affiliate.pk} value={affiliate.pk}>
                                {affiliate.name} {affiliate.group ? `(${affiliate.group})` : ''}
                            </MenuItem>
                        ))}
                    </TextField>
                ) : (
                    <TextField
                        label="Affiliate ID"
                        fullWidth
                        type="number"
                        value={config.affiliate || ''}
                        onChange={ev =>
                            updateConfig({ affiliate: parseInt(ev.target.value, 10) || null })
                        }
                        helperText="Test connection to load affiliates"
                    />
                )}
            </Grid>
            <Grid item xs={4}>
                {companyInfo !== null ? (
                    <TextField
                        select
                        label="Total Sheet"
                        fullWidth
                        value={config.totalSheet || ''}
                        onChange={ev => {
                            const newValues = { totalSheet: ev.target.value };
                            const selectedSheet = companyInfo.totalSheets?.find(
                                ts => ts.pk.toString() === ev.target.value.toString()
                            );
                            newValues['totalSheetUuid'] = selectedSheet ? selectedSheet.uuid : null;
                            updateConfig(newValues);
                        }}
                        helperText="Select default total sheet for bookings"
                    >
                        <MenuItem value="">
                            <em>Select a total sheet...</em>
                        </MenuItem>
                        {config.totalSheet &&
                            !companyInfo?.totalSheets?.some(
                                ts => ts.pk.toString() === config.totalSheet.toString()
                            ) && (
                                // e.g. the affiliate's base sheet, which /info doesn't list
                                <MenuItem value={config.totalSheet}>
                                    Sheet {config.totalSheet}
                                </MenuItem>
                            )}
                        {companyInfo?.totalSheets?.map(totalSheet => (
                            <MenuItem key={totalSheet.pk} value={totalSheet.pk}>
                                {totalSheet.name} {totalSheet.note ? `(${totalSheet.note})` : ''}
                            </MenuItem>
                        ))}
                    </TextField>
                ) : (
                    <TextField
                        label="Total Sheet ID"
                        fullWidth
                        type="number"
                        value={config.totalSheet || ''}
                        onChange={ev =>
                            updateConfig({ totalSheet: parseInt(ev.target.value, 10) || null })
                        }
                        helperText="Test connection to load total sheets"
                    />
                )}
            </Grid>
        </FormSection>
    );
}
